import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import PageLayout from '../PageLayout';
import PageHeader from '../PageHeader';
import CardActions from '../CardActions';
import './CardDetailsPage.css';

/**
 * A generic component for displaying card details with a customizable sidebar
 */
const CardDetailsPage = ({
  card,
  loading,
  error,
  cardNumber,
  title,
  imageUrl,
  description,
  navigationLinks,
  leftActions,
  rightActions,
  renderSidebar,
  alertMessage,
  onCloseAlert,
  breadcrumbItems,
  className = '',
  footer = null
}) => {
  if (loading) {
    return <div className="card-details__loading">Loading card details...</div>;
  }

  if (error) {
    return <div className="card-details__error">{error}</div>;
  }

  if (!card) {
    return <div className="card-details__error">Card not found</div>;
  }

  // Create header subtitle with card number if available
  const subtitle = cardNumber ? `Card #${cardNumber}` : '';

  // Create the top actions menu if we have actions
  const topActions = (leftActions || rightActions) ? (
    <CardActions
      leftActions={leftActions}
      rightActions={rightActions}
    />
  ) : null;

  return (
    <PageLayout 
      sidebar={renderSidebar()}
      topActionMenu={topActions}
      breadcrumbItems={breadcrumbItems}
      footer={footer}
    >
      <div className={`card-details ${className}`}>
        {/* Success message */}
        {alertMessage && (
          <div className="card-details__alert">
            {alertMessage}
            {onCloseAlert && (
              <button onClick={onCloseAlert} className="card-details__alert-close">×</button>
            )}
          </div>
        )}
        
        <PageHeader
          title={title}
          subtitle={subtitle}
          backButton={navigationLinks?.back}
        />
        
        <div className="card-details__main">
          {/* Card image */}
          <div className="card-details__image-container">
            {imageUrl ? (
              <img src={imageUrl} alt={title} className="card-details__image" />
            ) : (
              <div className="card-details__image-placeholder">
                <span>{title.charAt(0)}</span>
              </div>
            )}
          </div>
          
          {/* Card description */}
          {description && (
            <div className="card-details__description">
              <ReactMarkdown>{description}</ReactMarkdown>
            </div>
          )}
          
          {/* Navigation - only view link since back is in the header */}
          {navigationLinks && navigationLinks.view && (
            <div className="card-details__navigation">
              <Link to={navigationLinks.view.url} className="card-details__insert-link">
                {navigationLinks.view.label}
              </Link>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

CardDetailsPage.propTypes = {
  card: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  cardNumber: PropTypes.string,
  title: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  description: PropTypes.string,
  navigationLinks: PropTypes.shape({
    back: PropTypes.shape({
      url: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    }),
    view: PropTypes.shape({
      url: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  }),
  leftActions: PropTypes.node,
  rightActions: PropTypes.node,
  renderSidebar: PropTypes.func.isRequired,
  alertMessage: PropTypes.string,
  onCloseAlert: PropTypes.func,
  breadcrumbItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string
    })
  ),
  className: PropTypes.string
};

export default CardDetailsPage;